import React, { useState, useEffect } from 'react';
const useId = () => React.useMemo(() => "id_" + Math.random().toString(36).substr(2, 9), []);
import { GaugeIcon } from '../custom-widgets/IconAssets';
import sharedStyles from './ManagerShared.module.css';
import styles from './ManagerValueGauge.module.css';

export default function ManagerValueGauge({ value }) {
    const [animatedRotation, setAnimatedRotation] = useState(-90);
    const [isAnimating, setIsAnimating] = useState(false);
    const uniqueId = useId();
    const idSafe = uniqueId.replace(/:/g, ''); // Remove colons for SVG ID safety

    if (!value) return null;

    const percentage = typeof value.percent === 'number' ? value.percent / 100 : 0.5;
    const targetRotation = -90 + (percentage * 180);

    useEffect(() => {
        setIsAnimating(false);
        setAnimatedRotation(-90);

        const timer = setTimeout(() => {
            setIsAnimating(true);
            setAnimatedRotation(targetRotation);
        }, 100);
        return () => clearTimeout(timer);
    }, [targetRotation]);

    const renderDefs = (suffix) => {
        const isDesktop = suffix === 'desktop';
        const x1 = isDesktop ? "13.5" : "5";
        const y1 = isDesktop ? "105.5" : "100";
        const x2 = isDesktop ? "203.5" : "195";
        const y2 = isDesktop ? "105.5" : "100";

        return (
            <defs>
                <linearGradient
                    id={`goldGradient-${suffix}-${idSafe}`}
                    gradientUnits="userSpaceOnUse"
                    x1="50%"
                    y1="100%"
                    x2="50%"
                    y2="0%"
                >
                    <stop offset="0%" stopColor="#A76700" />
                    <stop offset="100%" stopColor="#D4AF37" />
                </linearGradient>

                <filter id={`goldGlow-${suffix}-${idSafe}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feFlood floodColor="var(--primary-color)" floodOpacity="0.4" result="color" />
                    <feComposite in="color" in2="blur" operator="in" result="glow" />
                    <feMerge>
                        <feMergeNode in="glow" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                {isDesktop ? (
                    <mask id={`activeMask-desktop-${idSafe}`}>
                        {[...Array(40)].map((_, i) => {
                            const angle = 180 - (i / 39) * 180;
                            const isActive = (i / 40) <= percentage;
                            const centerX = 108.5;
                            const centerY = 105.5;
                            const radiusX = 95;
                            const radiusY = 92;
                            const rad = (angle * Math.PI) / 180;
                            const x = (centerX + radiusX * Math.cos(rad)).toFixed(4);
                            const y = (centerY - radiusY * Math.sin(rad)).toFixed(4);
                            const normalAngle = (Math.atan2(radiusY * Math.cos(rad), radiusX * Math.sin(rad)) * 180 / Math.PI).toFixed(4);

                            return (
                                <g key={i} transform={`translate(${x}, ${y})`}>
                                    <rect
                                        x="-1.75"
                                        y="-13.5"
                                        width="3.5"
                                        height="27"
                                        fill="white"
                                        style={{
                                            opacity: isActive ? 1 : 0,
                                            transition: `opacity 0.5s ease ${i * 0.02}s`
                                        }}
                                        transform={`rotate(${normalAngle})`}
                                    />
                                </g>
                            );
                        })}
                    </mask>
                ) : (
                    <mask id={`activeMask-mobile-${idSafe}`}>
                        {[...Array(40)].map((_, i) => {
                            const angle = -90 + (i * 4.5 * 180) / (39 * 4.5);
                            const isActive = (i / 40) <= percentage;

                            return (
                                <rect
                                    key={i}
                                    x="97.5"
                                    y="0.5"
                                    width="3.5"
                                    height="27"
                                    fill="white"
                                    style={{
                                        transform: `rotate(${angle}deg)`,
                                        transformOrigin: '100px 100px',
                                        opacity: isActive ? 1 : 0,
                                        transition: `opacity 0.5s ease ${i * 0.02}s`
                                    }}
                                />
                            );
                        })}
                    </mask>
                )}
            </defs>
        );
    };

    const displayVal = parseFloat(value.current) || value.numericValue || 65.4;
    const centerVal = (typeof value.percent === 'number' && Number.isFinite(value.percent))
        ? Math.round(value.percent)
        : displayVal;

    return (
        <section className={sharedStyles.section}>
            <h2 className={sharedStyles.sectionTitle}>
                <span>Manager Pressure Rating</span>
            </h2>
            <div className={styles.container}>
                <div className={`${styles.gaugeSvgWrapper} ${styles.gaugeDesktop}`}>
                    <div className={styles.desktopGaugeRow}>
                        <div className={`${styles.desktopSideStat} ${styles.desktopSideLeft}`}>
                            <div className={styles.desktopSideLabel}>{String(value.floorLabel || 'PPM').toUpperCase()}</div>
                            <div className={styles.desktopSideValue}>{value.floor}</div>
                            <div className={styles.desktopSideSub}>Points per Match</div>
                        </div>

                        <div className={styles.desktopGaugeCenter} aria-label="Pressure gauge">
                            <svg xmlns="http://www.w3.org/2000/svg" width="217" height="106" viewBox="0 0 217 106" fill="none" className={styles.desktopGaugeSvg}>
                                {renderDefs('desktop')}
                                

                                
                                {/* Filled bars along ellipse arc */}
                                {[...Array(40)].map((_, i) => {
                                    // Calculate angle from 180 (left) to 0 (right) through top
                                    const angle = 180 - (i / 39) * 180;
                                    
                                    // Ellipse parameters
                                    const centerX = 108.5;
                                    const centerY = 105.5;
                                    const radiusX = 95;
                                    const radiusY = 92;
                                    
                                    // Convert angle to radians
                                    const rad = (angle * Math.PI) / 180;
                                    
                                    // Position on ellipse
                                    const x = (centerX + radiusX * Math.cos(rad)).toFixed(4);
                                    const y = (centerY - radiusY * Math.sin(rad)).toFixed(4);
                                    
                                    // Normal vector perpendicular to ellipse (pointing outward)
                                    const normalAngle = (Math.atan2(radiusY * Math.cos(rad), radiusX * Math.sin(rad)) * 180 / Math.PI).toFixed(4);

                                    return (
                                        <g key={i} transform={`translate(${x}, ${y})`}>
                                            <rect
                                                x="-1.75"
                                                y="-13.5"
                                                width="3.5"
                                                height="27"
                                                fill="#333333"
                                                style={{
                                                    opacity: 0.3
                                                }}
                                                transform={`rotate(${normalAngle})`}
                                            />
                                        </g>
                                    );
                                })}

                                <g filter={`url(#goldGlow-desktop-${idSafe})`}>
                                    <rect
                                        x="0"
                                        y="0"
                                        width="217"
                                        height="106"
                                        fill={`url(#goldGradient-desktop-${idSafe})`}
                                        mask={`url(#activeMask-desktop-${idSafe})`}
                                    />
                                </g>

                                <g transform="translate(108.5, 86)">
                                    <text
                                        fill="var(--primary-color)"
                                        fontSize="40"
                                        fontWeight="700"
                                        fontFamily="Barlow Condensed, sans-serif"
                                        textAnchor="middle"
                                        style={{
                                            lineHeight: '32px',
                                            letterSpacing: '-1.6px'
                                        }}
                                    >
                                    {centerVal}
                                    </text>
                                    <text
                                        y="15"
                                        fill="var(--primary-color)"
                                        fontSize="11"
                                        fontWeight="700"
                                        fontFamily="Barlow Condensed, sans-serif"
                                        textAnchor="middle"
                                        style={{
                                            opacity: 0.5,
                                            letterSpacing: '-0.44px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Pressure Rating
                                    </text>
                                </g>
                            </svg>

                            <div className={styles.desktopStatus}>UNDER PRESSURE</div>
                        </div>

                        <div className={`${styles.desktopSideStat} ${styles.desktopSideRight}`}>
                            <div className={styles.desktopSideLabel}>{String(value.ceilingLabel || 'WIN%').toUpperCase()}</div>
                            <div className={styles.desktopSideValue}>{value.ceiling}</div>
                            <div className={styles.desktopSideSub}>Percentage</div>
                        </div>
                    </div>
                </div>

                <div className={`${styles.gaugeSvgWrapper} ${styles.gaugeMobile}`}>
                    <div className={styles.mobileGaugeRow}>
                        <div className={styles.mobileSideStat}>
                            <div className={styles.mobileSideLabel}>{String(value.floorLabel || 'PPM').toUpperCase()}</div>
                            <div className={styles.mobileSideValue}>{value.floor}</div>
                            <div className={styles.mobileSideSub}>Points per Match</div>
                        </div>

                        <div className={styles.mobileGaugeCenter} aria-label="Pressure gauge">
                            <svg viewBox="0 0 200 110" className={styles.mobileGaugeSvg}>
                                {renderDefs('mobile')}

                                {[...Array(40)].map((_, i) => {
                                    const angle = -90 + (i * 4.5 * 180) / (39 * 4.5);

                                    return (
                                        <rect
                                            key={i}
                                            x="97.5"
                                            y="0.5"
                                            width="3.5"
                                            height="27"
                                            fill="#2a2a2a"
                                            style={{
                                                transform: `rotate(${angle}deg)`,
                                                transformOrigin: '100px 100px',
                                                opacity: 0.4
                                            }}
                                        />
                                    );
                                })}

                                <g filter={`url(#goldGlow-mobile-${idSafe})`}>
                                    <rect
                                        x="0"
                                        y="0"
                                        width="200"
                                        height="110"
                                        fill={`url(#goldGradient-mobile-${idSafe})`}
                                        mask={`url(#activeMask-mobile-${idSafe})`}
                                    />
                                </g>

                                <g transform="translate(100, 82)">
                                    <text
                                        fill="var(--primary-color)"
                                        fontSize="44"
                                        fontWeight="700"
                                        fontFamily="Barlow Condensed, sans-serif"
                                        textAnchor="middle"
                                        style={{
                                            letterSpacing: '-1.76px'
                                        }}
                                    >
                                    {centerVal}
                                    </text>
                                    <text
                                        y="20"
                                        fill="var(--primary-color)"
                                        fontSize="12"
                                        fontWeight="700"
                                        fontFamily="Barlow Condensed, sans-serif"
                                        textAnchor="middle"
                                        style={{
                                            opacity: 0.5,
                                            letterSpacing: '-0.48px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Pressure rating
                                    </text>
                                </g>
                            </svg>

                            <div className={styles.mobileStatus}>UNDER PRESSURE</div>
                        </div>

                        <div className={`${styles.mobileSideStat} ${styles.mobileSideRight}`}>
                            <div className={styles.mobileSideLabel}>{String(value.ceilingLabel || 'WIN%').toUpperCase()}</div>
                            <div className={styles.mobileSideValue}>{value.ceiling}</div>
                            <div className={styles.mobileSideSub}>Percentage</div>
                        </div>
                    </div>
                </div>
        </div>
        </section>
    );
}
